import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyContext } from '@context/SurveyContext'
import { ConsentContext } from '@context/ConsentContext'
import { ResponseMeta } from '@models/Common'
import { ConsentResponse } from '@models/Identity'
import {
  AnswerObject,
  FlatQuestion,
  getQuestionChoices,
  OneOfManyQuestion,
  QuestionType,
  questionHasChoiceList,
  withChoicesFromApi,
} from '@models/Survey'
import { PredictionData } from '@models/Prediction/Prediction'
import { SubmitSandboxRequest, SubmitSurveyRequest } from '@models/Survey/SubmitAnswersRequest'
import API from '@api/client'
import PredictionReducer, {
  initialState as predictionInitialState,
  PredictionActionType,
  PredictionState,
} from '@reducers/PredictionReducer'
import SurveyReducer, {
  initialState as surveyInitialState,
  SurveyActionType,
  SurveyMode,
  SurveyState,
} from '@reducers/SurveyReducer'
import { surveyStorage, SurveyStorageKeys } from '@plugins/storage'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { SurveyStatAction } from '@models/Survey/SurveyStatRequest'
import { submitStats } from '@utils/stats/stats'
import debounce from 'lodash/debounce'
import { AnswerType } from '@models/Survey/Answer'
import dayjs from '@utils/stats/date'
import { APP_SLUG, isUSAApp } from '@models/AppType'
import { getCategoryQuestions } from '@models/Survey'
import type { Category, Survey } from '@models/Survey/Survey'
import { useTranslation } from 'react-i18next'
import { changeLanguage } from '@utils/language'
import AppLoader from '@components/AppLoader/AppLoader'
import { getNextSurveyKey, QuestionnairePhase } from '@utils/categoryFlow'
import { isPredictionResults } from '@components/Survey/TabbedPane/ResultsChartView'
import { setAppTypeOverride } from '@models/AppType'
import { getAdminPreviewSurveyKey } from '../dev/applyAdminPreviewQuery'

function WithSurvey<P extends JSX.IntrinsicAttributes>(WrappedComponent: FunctionComponent<P>): FunctionComponent<P> {
  const ComponentWithSurvey: FunctionComponent<P> = (props) => {
    const { pathname } = window.location
    const { i18n, t } = useTranslation()
    const [isLoading, setIsLoading] = useState(true)
    const [questionStartDate, setQuestionStartDate] = useState(dayjs())
    const [surveyState, surveyDispatch] = useReducer(SurveyReducer, surveyInitialState)
    const [predictionState, predictionDispatch] = useReducer(PredictionReducer, predictionInitialState)
    const { consentData } = useContext(ConsentContext)
    const isDefaultMode = useMemo(() => surveyState.mode === SurveyMode.DEFAULT, [surveyState.mode])

    const initState = async () => {
      const lookup = getAdminPreviewSurveyKey() || APP_SLUG
      if (!lookup) {
        setIsLoading(true)
        return
      }

      await fetchSurvey(lookup)
      await loadSurveyProgress()
      await loadPrediction()
      setIsLoading(false)
    }

    useEffect(() => {
      if (isUSAApp && i18n.language.includes('fr')) {
        changeLanguage(i18n, 'en').then(() => {
          return
        })
      }
    }, [i18n])

    useEffect(() => {
      initState()
    }, [])

    useEffect(() => {
      if (isLoading || !pathname.includes('/survey/answer')) {
        return
      }

      if (surveyState.currentQuestion?.id) {
        const date = dayjs()
        setQuestionStartDate(dayjs())
        submitStats(SurveyStatAction.QuestionStart, surveyState, date, consentData)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyState.currentQuestion?.id, isLoading, pathname, consentData])

    useEffect(() => {
      if (isLoading || !consentData || !isDefaultMode) {
        return
      }

      const { hasSentStartSurvey, startedSurveyAt } = surveyState

      if (!startedSurveyAt || hasSentStartSurvey) {
        return
      }

      surveyDispatch({ type: SurveyActionType.SetHasSentStartSurvey, payload: true })
      submitStats(SurveyStatAction.SurveyStart, surveyState, dayjs(startedSurveyAt), consentData)
    }, [isLoading, surveyState, consentData, isDefaultMode])

    useEffect(() => {
      if (isLoading) {
        return
      }

      surveyDispatch({ type: SurveyActionType.SetSurveyProgress, payload: surveyState })
    }, [isLoading, surveyState])

    useEffect(() => {
      if (isLoading || !consentData) {
        return
      }

      surveyStorage.setItem(SurveyStorageKeys.PREDICTION_DATA, predictionState)
    }, [consentData, isLoading, predictionState])

    const applyCategoryI18nOverrides = (surveyData: { category?: Category | null } | null | undefined) => {
      const category = surveyData?.category
      const appTypeKey = category?.appType ?? category?.app_type ?? null
      setAppTypeOverride(appTypeKey)
      const overrides = category?.uiI18nOverrides ?? category?.ui_i18n_overrides ?? null
      if (!overrides) return
      void import('../i18n/applyEditionI18nOverrides').then(({ applyEditionI18nOverrides }) => {
        applyEditionI18nOverrides(overrides)
      })
    }

    const fetchSurvey = (surveyId: string): Promise<void> => {
      surveyDispatch({ type: SurveyActionType.FetchSurveyPending })
      return API.getSurvey(surveyId)
        .then(({ data }) => {
          surveyDispatch({ type: SurveyActionType.FetchSurveySuccess, payload: data })
          setIsEnabled(true)
          applyCategoryI18nOverrides(data)
        })
        .catch((error: ResponseMeta) => {
          surveyDispatch({ type: SurveyActionType.FetchSurveyFailure, payload: error.message })
        })
    }

    const autofillSurvey = (): void => {
      const questions = getCategoryQuestions(surveyState.survey?.category).map(withChoicesFromApi)

      if (
        import.meta.env.VITE_APP_BUILD_ENV === 'local' ||
        import.meta.env.VITE_APP_BUILD_ENV === 'development' ||
        import.meta.env.VITE_APP_BUILD_ENV === 'demo' ||
        import.meta.env.VITE_APP_BUILD_ENV === 'staging'
      ) {
        for (const question of questions) {
          setQuestion(question.id)
          switch (question.type) {
            case QuestionType.Country:
              setAnswer({
                code: 'ca',
                artworkUrl: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/ca.svg',
                pixelatedArtworkUrl:
                  'data:image/png;base64,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',
              } as unknown as string)
              break
            case QuestionType.FreeText:
              if (questionHasChoiceList(question)) {
                const choices = getQuestionChoices(question)
                const choiceIndex = Math.floor(Math.random() * choices.length)
                setAnswer(choices[choiceIndex].id)
              } else {
                setAnswer('Text')
              }
              break
            case QuestionType.PostalCode:
              setAnswer({
                code: 'G0A3K0',
                nmCirc: 'Montmorency',
              } as unknown as string)
              break
            case QuestionType.OneOfMany:
            case QuestionType.OneOfManyGraphic: {
              const choices = getQuestionChoices(question as OneOfManyQuestion)
              if (!choices.length) {
                break
              }
              const choiceIndex = Math.floor(Math.random() * choices.length)
              const value = choices[choiceIndex].id

              if (question.key === 'tactical') {
                setAnswer({
                  value: value,
                  reference: 'AUTOFILL',
                } as unknown as string)
              } else {
                setAnswer(value)
              }

              break
            }

            case QuestionType.Integer: {
              const number = Math.floor(Math.random() * 99)
              setAnswer(number as unknown as string)
              break
            }

            case QuestionType.Movie:
              setAnswer({
                id: '680',
                term: 'Fict',
                title: 'Fiction pulpeuse',
                genre: 'THRILLER',
                artworkUrl: 'https://image.tmdb.org/t/p/w200/4TBdF7nFw2aKNM0gPOlDNq3v3se.jpg',
                pixelatedArtworkUrl:
                  'data:image/png;base64,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',
                selectedGenre: 'THRILLER',
              } as unknown as string)
              break
            case QuestionType.Music:
              setAnswer({
                id: 'f1afec0b-26dd-3db5-9aa1-c91229a74a24',
                term: 'Nirv',
                artist: 'Nirvana',
                title: 'Bleach',
                genre: 'HARD-ROCK',
                artworkUrl: 'https://coverartarchive.org/release-group/f1afec0b-26dd-3db5-9aa1-c91229a74a24/front-250',
                pixelatedArtworkUrl:
                  'data:image/png;base64,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',
                selectedGenre: 'ELECTRO',
              } as unknown as string)
              break
            case QuestionType.Book:
              setAnswer({
                id: '2172250',
                isbn: '9782014001327',
                pixelatedThumbnailUrl:
                  'data:image/png;base64,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',
                thumbnailUrl: 'https://images.leslibraires.ca/books/9782014001327/front/9782014001327_medium.jpg',
                title: "Une aventure d'Astérix T.8 : Astérix chez les Bretons",
              } as unknown as string)
              break
          }
        }
      }
    }

    const loadSurveyProgress = async () => {
      const storedAnswers = await surveyStorage.getItem<SurveyState>(SurveyStorageKeys.ANSWER_DATA)
      if (storedAnswers) {
        surveyDispatch({ type: SurveyActionType.LoadSurveyProgress, payload: storedAnswers })
      }
    }

    const loadPrediction = async () => {
      const storedAnswers = await surveyStorage.getItem<PredictionState>(SurveyStorageKeys.PREDICTION_DATA)

      if (storedAnswers) {
        predictionDispatch({ type: PredictionActionType.SetState, payload: storedAnswers })
      }
    }

    const setPreviousQuestion = (): void => {
      surveyDispatch({ type: SurveyActionType.SetPreviousQuestion })
      submitStats(SurveyStatAction.QuestionPrev, surveyState, questionStartDate, consentData)
    }

    const setNextQuestion = (): void => {
      surveyDispatch({ type: SurveyActionType.SetNextQuestion })
      submitStats(SurveyStatAction.QuestionNext, surveyState, questionStartDate, consentData)
    }

    const setAnswerStat = useCallback(
      (value: AnswerType) => {
        const isObject = value !== null && typeof value === 'object'

        const wantedValues = !isObject ? value : { ...(value as Record<string, unknown>) }

        if ((wantedValues as Record<string, unknown>).pixelatedArtworkUrl) {
          delete (wantedValues as Record<string, unknown>).pixelatedArtworkUrl
        }

        submitStats(SurveyStatAction.AnswerStat, surveyState, questionStartDate, consentData, wantedValues)
      },
      [consentData, questionStartDate, surveyState],
    )

    const setAnswerStatThrottle = useMemo(() => debounce(setAnswerStat, 3000), [setAnswerStat])

    const setAnswer = (value: Record<symbol, unknown> | string) => {
      if (isDefaultMode) {
        setAnswerStatThrottle(value)
      }

      surveyDispatch({ type: SurveyActionType.SetCurrentAnswer, payload: value })
    }

    const setAnswerObject = (value: AnswerObject) => {
      surveyDispatch({ type: SurveyActionType.SetAnswerObject, payload: value })
    }

    const setQuestion = (slug: string): void => {
      surveyDispatch({ type: SurveyActionType.SetCurrentQuestion, payload: slug })
    }

    const setAcknowledge = (acknowledged: boolean): void => {
      surveyDispatch({ type: SurveyActionType.SetAcknowledge, payload: acknowledged })
    }

    const setMode = (mode: SurveyMode): void => {
      surveyDispatch({ type: SurveyActionType.SetMode, payload: mode })
    }

    const setNextStep = (nextStep: SurveyStep): void =>
      surveyDispatch({ type: SurveyActionType.SetStep, payload: nextStep })

    const setIsEnabled = (status: boolean): void => {
      return surveyDispatch({ type: SurveyActionType.SetIsEnabled, payload: status })
    }

    const answersByKey = (obj: AnswerObject) => {
      const { questionsList } = surveyState
      return Object.keys(obj).reduce((acc: AnswerObject, id: string) => {
        const question = questionsList.find(({ id: questionId }) => id === questionId)
        if (!question?.key) {
          return acc
        }
        const value = obj[id]
        if (typeof value === 'object' && value !== null) {
          const data = { ...value } as Record<string, unknown>

          if (data.pixelatedArtworkUrl) {
            delete data.pixelatedArtworkUrl
          }
          if (data.pixelatedThumbnailUrl) {
            delete data.pixelatedThumbnailUrl
          }

          acc[question.key] = data
        } else {
          acc[question.key] = obj[id]
        }
        return acc
      }, {})
    }

    // the prediction endpoint expect an object with question keys
    const preparePredictAnswers = (answers: AnswerObject): AnswerObject => {
      return answersByKey(answers)
    }

    const getSubmitAnswersRequest = (): SubmitSurveyRequest | null => {
      if (!consentData?.visitorId || consentData.surveyId == null) {
        return null
      }
      const { visitorId, surveyId } = consentData
      const { hasAcknowledged, answerObject, survey } = surveyState
      const targetSurveyId = survey?.id ?? surveyId

      return {
        answers: preparePredictAnswers(answerObject),
        visitorId,
        surveyId,
        targetSurveyId,
        confirmation: hasAcknowledged ?? false,
        language: i18n.language,
      }
    }

    const getSubmitSandboxRequest = (): SubmitSandboxRequest | null => {
      if (!consentData?.visitorId || consentData.surveyId == null) {
        return null
      }
      const { visitorId, surveyId } = consentData
      const { answerObject, survey } = surveyState
      return {
        answers: preparePredictAnswers(answerObject),
        visitorId,
        surveyId,
        targetSurveyId: survey?.id ?? surveyId,
      }
    }

    const getResetSandboxRequest = (answerObject: AnswerObject): SubmitSandboxRequest | null => {
      if (!consentData?.visitorId || consentData.surveyId == null) {
        return null
      }
      const { visitorId, surveyId } = consentData
      const { survey } = surveyState
      return {
        answers: preparePredictAnswers(answerObject),
        visitorId,
        surveyId,
        targetSurveyId: survey?.id ?? surveyId,
      }
    }

    const submitAnswers = (): Promise<void> => {
      const submitAnswersRequest = getSubmitAnswersRequest()
      if (!submitAnswersRequest) {
        return Promise.resolve()
      }

      predictionDispatch({ type: PredictionActionType.FetchPredictionPending })

      submitStats(SurveyStatAction.SurveyEnd, surveyState, dayjs(surveyState.startedSurveyAt), consentData)

      return API.submitAnswers(submitAnswersRequest)
        .then(({ data }) => {
          // activate this line to test predictionBars
          // predictionDispatch({ type: PredictionActionType.FetchPredictionSuccess, payload: percentageTestData })
          predictionDispatch({ type: PredictionActionType.FetchPredictionSuccess, payload: data })
          const surveyKey = surveyState.survey?.key
          if (surveyKey) {
            surveyDispatch({
              type: SurveyActionType.SetResultsSnapshot,
              payload: { surveyKey, snapshot: data },
            })
          }
        })
        .catch((error: ResponseMeta): void =>
          predictionDispatch({
            type: PredictionActionType.FetchPredictionFailure,
            payload: error.message,
          }),
        )
    }

    const submitEmail = (email: string): Promise<void> => {
      const emailData = {
        email,
      }
      surveyDispatch({ type: SurveyActionType.SetEmail, payload: email })
      return API.submitEmail(emailData)
        .then(() => {})
        .catch((): void => {})
    }

    const setEmail = (email: string): void => {
      surveyDispatch({ type: SurveyActionType.SetEmail, payload: email })
    }

    const setPortraitForSurvey = (surveyKey: string, dataUrl: string): void => {
      surveyDispatch({
        type: SurveyActionType.SetPortraitForSurvey,
        payload: { surveyKey, dataUrl },
      })
    }

    const setResultsSnapshot = (surveyKey: string, snapshot: unknown): void => {
      surveyDispatch({
        type: SurveyActionType.SetResultsSnapshot,
        payload: { surveyKey, snapshot },
      })
    }

    const submitSandbox = (): Promise<void> => {
      const submitSandboxRequest = getSubmitSandboxRequest()
      if (!submitSandboxRequest) {
        return Promise.resolve()
      }
      predictionDispatch({ type: PredictionActionType.FetchSandboxedPredictionPending })
      return API.submitSandbox(submitSandboxRequest)
        .then(({ data }) => {
          predictionDispatch({ type: PredictionActionType.FetchSandboxedPredictionSuccess, payload: data })
        })
        .catch((error: ResponseMeta): void =>
          predictionDispatch({
            type: PredictionActionType.FetchSandboxedPredictionFailure,
            payload: error.message,
          }),
        )
    }

    const resetSandbox = (answers: AnswerObject): Promise<void> => {
      const resetSandboxRequest = getResetSandboxRequest(answers)
      if (!resetSandboxRequest) {
        return Promise.resolve()
      }
      predictionDispatch({ type: PredictionActionType.FetchSandboxedPredictionPending })
      surveyDispatch({ type: SurveyActionType.ResetSandbox, payload: answers })
      return API.submitSandbox(resetSandboxRequest)
        .then(({ data }) => {
          predictionDispatch({ type: PredictionActionType.FetchSandboxedPredictionSuccess, payload: data })
        })
        .catch((error: ResponseMeta): void =>
          predictionDispatch({
            type: PredictionActionType.FetchSandboxedPredictionFailure,
            payload: error.message,
          }),
        )
    }

    // prediction

    const setPredictionVeracity = (veracity?: boolean): void => {
      predictionDispatch({ type: PredictionActionType.SetVeracity, payload: veracity })
    }

    const setRectifiedPrediction = (rectifiedPrediction: PredictionData): void => {
      predictionDispatch({ type: PredictionActionType.SetRectifiedPrediction, payload: rectifiedPrediction })
    }

    const setCitizenForecasting = (citizenForecasting: PredictionData): void => {
      predictionDispatch({ type: PredictionActionType.SetCitizenForecasting, payload: citizenForecasting })
    }

    const submitRectification = ({ id: correction }: PredictionData): void => {
      const { visitorId, surveyId } = consentData as ConsentResponse
      predictionDispatch({ type: PredictionActionType.FetchPredictionPending })
      API.submitCorrection({
        visitorId,
        surveyId,
        correction,
      })

      predictionDispatch({ type: PredictionActionType.SubmitRectificationSuccess })
    }

    const submitForecasting = (): void => {
      const { visitorId, surveyId } = consentData as ConsentResponse
      const { citizenForecasting } = predictionState
      if (citizenForecasting?.id) {
        API.submitForecasting({
          visitorId,
          surveyId,
          party: String(citizenForecasting?.id),
        })
      }
    }

    const submitVoteProbability = () => {
      const { visitorId, surveyId } = consentData as ConsentResponse
      const { voteProbability } = predictionState

      if (voteProbability != null) {
        API.submitProbability({
          visitorId,
          surveyId,
          probability: voteProbability ?? null,
        })
      }
    }

    const setVoteProbability = (probability: number) =>
      predictionDispatch({ type: PredictionActionType.SetVoteProbability, payload: probability })

    const setSurveyStartedAt = (date: string) => {
      surveyDispatch({ type: SurveyActionType.SetStartedSurveyAt, payload: date })
    }

    const submitCompleteSurvey = async () => {
      const { visitorId, surveyId } = consentData as ConsentResponse
      await API.submitCompleteSurvey({ visitorId, surveyId })
    }

    const setResultVariant = (variant: 'intermediate' | 'final' | undefined): void => {
      surveyDispatch({ type: SurveyActionType.SetResultVariant, payload: variant })
    }

    const setQuestionnairePhase = (phase: QuestionnairePhase): void => {
      surveyDispatch({ type: SurveyActionType.SetQuestionnairePhase, payload: phase })
    }

    const continueToSecondQuestionnaire = (): Promise<void> => {
      const nextKey = getNextSurveyKey(surveyState.survey)
      if (!nextKey) {
        return Promise.reject(new Error('No next questionnaire in category'))
      }
      surveyDispatch({ type: SurveyActionType.FetchSurveyPending })
      return API.getSurvey(nextKey)
        .then(({ data }) => {
          surveyDispatch({
            type: SurveyActionType.PrepareSecondQuestionnaire,
            payload: data,
          })
          setIsEnabled(true)
          applyCategoryI18nOverrides(data)
        })
        .catch((error: ResponseMeta) => {
          surveyDispatch({
            type: SurveyActionType.FetchSurveyFailure,
            payload: error.message,
          })
        })
    }

    const switchSandboxSurvey = (surveyKey: string): Promise<void> => {
      if (!surveyKey || surveyKey === surveyState.survey?.key) {
        return Promise.resolve()
      }
      if (!consentData?.visitorId || consentData.surveyId == null) {
        return Promise.resolve()
      }
      const { visitorId, surveyId } = consentData
      const restoredAnswers = surveyState.answersBySurveyKey?.[surveyKey] ?? {}

      const applySwitchAndPredict = (survey: Survey): Promise<void> => {
        surveyDispatch({
          type: SurveyActionType.SwitchSandboxSurvey,
          payload: { survey, answers: restoredAnswers },
        })
        applyCategoryI18nOverrides(survey)
        // Show this questionnaire's frozen mini-chart immediately (intermediate vs final).
        const snapshot = survey.key
          ? surveyState.resultsSnapshotsBySurveyKey?.[survey.key]
          : undefined
        if (isPredictionResults(snapshot)) {
          predictionDispatch({
            type: PredictionActionType.FetchSandboxedPredictionSuccess,
            payload: snapshot,
          })
        } else {
          predictionDispatch({ type: PredictionActionType.FetchSandboxedPredictionPending })
        }
        return API.submitSandbox({
          answers: preparePredictAnswers(restoredAnswers),
          visitorId,
          surveyId,
          targetSurveyId: survey.id ?? surveyId,
        })
          .then(({ data }) => {
            predictionDispatch({
              type: PredictionActionType.FetchSandboxedPredictionSuccess,
              payload: data,
            })
          })
          .catch((error: ResponseMeta): void =>
            predictionDispatch({
              type: PredictionActionType.FetchSandboxedPredictionFailure,
              payload: error.message,
            }),
          )
      }

      const cached = surveyState.surveyCacheByKey?.[surveyKey]
      if (cached) {
        return applySwitchAndPredict(cached)
      }
      surveyDispatch({ type: SurveyActionType.FetchSurveyPending })
      return API.getSurvey(surveyKey)
        .then(({ data }) => {
          setIsEnabled(true)
          return applySwitchAndPredict(data)
        })
        .catch((error: ResponseMeta) => {
          surveyDispatch({
            type: SurveyActionType.FetchSurveyFailure,
            payload: error.message,
          })
        })
    }

    if (isLoading) {
      return <AppLoader />
    }

    return (
      <SurveyContext.Provider
        value={{
          ...surveyState,
          setAnswer,
          setAcknowledge,
          setMode,
          setNextQuestion,
          setPreviousQuestion,
          setQuestion,
          submitAnswers,
          submitEmail,
          submitSandbox,
          submitCompleteSurvey,
          fetchSurvey,
          setAnswerObject,
          setNextStep,
          setIsEnabled,
          setSurveyStartedAt,
          autofillSurvey,
          resetSandbox,
          continueToSecondQuestionnaire,
          switchSandboxSurvey,
          setResultVariant,
          setQuestionnairePhase,
          setEmail,
          setPortraitForSurvey,
          setResultsSnapshot,
        }}>
        <PredictionContext.Provider
          value={{
            ...predictionState,
            setPredictionVeracity,
            setRectifiedPrediction,
            setCitizenForecasting,
            submitRectification,
            submitForecasting,
            submitVoteProbability,
            setVoteProbability,
          }}>
          <WrappedComponent {...props} />
        </PredictionContext.Provider>
      </SurveyContext.Provider>
    )
  }

  return ComponentWithSurvey
}

export default WithSurvey
